import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton, NewsCardSkeleton, StatCardSkeleton } from '../components/ui/skeleton';
import { 
  Newspaper, TrendingUp, FileText, Users, ArrowRight, Clock, 
  Globe, Ship, Plane, Package, MessageSquare, Zap, Activity,
  ChevronRight, Sparkles, BarChart3, Building2, Plus, Search, Megaphone
} from 'lucide-react';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl, isValidImageUrl } from '../utils/imageUrl';
import { getMediumImageUrl, getThumbnailUrl } from '../utils/cloudinaryUpload';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentRfqs, setRecentRfqs] = useState([]);
  const [networkStats, setNetworkStats] = useState({ active_members: 0, countries: 0, active_rfqs: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      const [newsRes, quotesRes, rfqsRes, statsRes] = await Promise.all([
        api.get('/news').catch(() => ({ data: [] })),
        api.get('/quotes/recent').catch(() => ({ data: [] })),
        api.get('/rfqs').catch(() => ({ data: [] })),
        api.get('/network/stats').catch(() => ({ data: { active_members: 0, countries: 0, active_rfqs: 0 } }))
      ]);
      
      setNews(newsRes.data?.slice(0, 4) || []);
      setRecentQuotes(quotesRes.data?.slice(0, 5) || []);
      setRecentRfqs(rfqsRes.data?.slice(0, 5) || []);
      setNetworkStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load home data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sample news for when no real news exists
  const sampleNews = [
    {
      id: '1',
      title: 'Caribbean Shipping Outlook 2025',
      excerpt: 'Industry experts predict significant growth in Caribbean maritime trade.',
      featured_image: 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=400',
      tags: ['Industry'],
      published_at: new Date().toISOString()
    },
    {
      id: '2',
      title: 'New Air Cargo Routes Announced',
      excerpt: 'Major airlines expand cargo services across key Caribbean destinations.',
      featured_image: 'https://images.pexels.com/photos/358319/pexels-photo-358319.jpeg?auto=compress&cs=tinysrgb&w=400',
      tags: ['Air Freight'],
      published_at: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  const displayNews = news.length > 0 ? news : sampleNews;

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
            {/* Skeleton Welcome */}
            <div className="mb-8">
              <Skeleton className="h-8 w-48 mb-3 bg-slate-700" />
              <Skeleton className="h-4 w-96 bg-slate-700" />
            </div>
            
            {/* Skeleton Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-slate-800/50 rounded-xl p-4">
                  <Skeleton className="h-8 w-16 mb-2 bg-slate-700" />
                  <Skeleton className="h-3 w-24 bg-slate-700" />
                </div>
              ))}
            </div>
            
            {/* Skeleton Content */}
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-slate-800/30 rounded-2xl p-6">
                  <Skeleton className="h-6 w-32 mb-6 bg-slate-700" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="bg-slate-900/50 rounded-xl overflow-hidden">
                        <Skeleton className="h-32 w-full bg-slate-700" />
                        <div className="p-4 space-y-2">
                          <Skeleton className="h-4 w-3/4 bg-slate-700" />
                          <Skeleton className="h-3 w-1/2 bg-slate-700" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-slate-800/30 rounded-2xl p-6">
                  <Skeleton className="h-6 w-32 mb-4 bg-slate-700" />
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-10 w-full mb-2 bg-slate-700" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section with Grid Pattern */}
        <div className="relative overflow-hidden">
          {/* Animated Grid Background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
          
          {/* Glowing Orbs */}
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
            {/* Welcome Banner */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 text-sm font-mono">LIVE NETWORK</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white mb-3">
                Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{user?.full_name?.split(' ')[0]}</span>
              </h1>
              <p className="text-slate-400 text-base sm:text-lg max-w-2xl">
                Your central hub for Caribbean freight intelligence, quotes, and network activity.
              </p>
            </div>

            {/* Live Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
              <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 hover:border-cyan-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg group-hover:bg-cyan-500/20 transition-colors">
                    <Users className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{networkStats.active_members || 0}</p>
                    <p className="text-xs text-slate-500">Active Members</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 hover:border-green-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg group-hover:bg-green-500/20 transition-colors">
                    <Globe className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{networkStats.countries || 0}</p>
                    <p className="text-xs text-slate-500">Countries</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 hover:border-purple-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                    <FileText className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{networkStats.active_rfqs || 0}</p>
                    <p className="text-xs text-slate-500">Active RFQs</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 hover:border-orange-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg group-hover:bg-orange-500/20 transition-colors">
                    <Activity className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">Live</p>
                    <p className="text-xs text-slate-500">Network Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Left Column - News Feed */}
            <div className="lg:col-span-2 space-y-6">
              {/* News Section */}
              <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg">
                      <Newspaper className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white">Latest News</h2>
                      <p className="text-xs text-slate-500">Industry updates & insights</p>
                    </div>
                  </div>
                  <Link to="/news">
                    <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                      View All <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {displayNews.slice(0, 4).map((article, index) => {
                    // Use fallback image if no valid featured_image
                    const fallbackImages = [
                      'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/358319/pexels-photo-358319.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg?auto=compress&cs=tinysrgb&w=400',
                      'https://images.pexels.com/photos/2226458/pexels-photo-2226458.jpeg?auto=compress&cs=tinysrgb&w=400'
                    ];
                    const rawImageUrl = isValidImageUrl(article.featured_image) ? toFullUrl(article.featured_image) : fallbackImages[index % fallbackImages.length];
                    // Apply Cloudinary optimization if it's a Cloudinary URL
                    const imageUrl = getMediumImageUrl(rawImageUrl, 400);
                    
                    return (
                    <Link 
                      key={article.id} 
                      to={`/news/${article.id}`}
                      className="group"
                    >
                      <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-cyan-500/50 transition-all hover:transform hover:scale-[1.02]">
                        <div className="relative h-32 overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
                          {article.tags?.[0] && (
                            <Badge className="absolute top-2 left-2 bg-cyan-500/80 text-white text-xs border-0">
                              {article.tags[0]}
                            </Badge>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-white line-clamp-2 group-hover:text-cyan-400 transition-colors">
                            {article.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <Clock className="h-3 w-3" />
                            {new Date(article.published_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );})}
                </div>
              </div>

              {/* Recent RFQs */}
              <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white">Open RFQs</h2>
                      <p className="text-xs text-slate-500">Latest freight requests</p>
                    </div>
                  </div>
                  <Link to="/rfqs">
                    <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10">
                      View All <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>

                <div className="space-y-3">
                  {recentRfqs.length > 0 ? recentRfqs.slice(0, 5).map((rfq) => (
                    <div 
                      key={rfq.id}
                      onClick={() => navigate(`/rfqs/${rfq.id}`)}
                      className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 hover:border-purple-500/50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {rfq.freight_type === 'air' ? (
                              <Plane className="h-4 w-4 text-blue-400" />
                            ) : (
                              <Ship className="h-4 w-4 text-cyan-400" />
                            )}
                            <span className="text-sm font-medium text-white group-hover:text-purple-400 transition-colors truncate">
                              {rfq.title || rfq.cargo_type}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="truncate">{rfq.origin}</span>
                            <ArrowRight className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{rfq.destination}</span>
                          </div>
                        </div>
                        <Badge className={`text-xs flex-shrink-0 ${
                          rfq.status === 'open' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          'bg-slate-500/20 text-slate-400 border-slate-500/30'
                        }`}>
                          {rfq.status}
                        </Badge>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-10 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20">
                      <div className="p-4 bg-purple-500/10 rounded-full w-fit mx-auto mb-4">
                        <Package className="h-10 w-10 text-purple-400" />
                      </div>
                      <h3 className="text-white font-medium mb-1">No Active RFQs</h3>
                      <p className="text-slate-400 text-sm mb-4 max-w-xs mx-auto">
                        Be the first to post a freight request and connect with carriers across the Caribbean
                      </p>
                      <Button 
                        onClick={() => navigate('/rfqs/create')}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create RFQ
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">Quick Actions</h2>
                </div>
                
                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/rfqs/create')}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white justify-start group"
                  >
                    <FileText className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                    Create New RFQ
                  </Button>
                  <Button 
                    onClick={() => navigate('/quotes/create')}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white justify-start group"
                  >
                    <TrendingUp className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                    Submit a Quote
                  </Button>
                  <Button 
                    onClick={() => navigate('/directory')}
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-white justify-start"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Browse Directory
                  </Button>
                  <Button 
                    onClick={() => navigate('/insights')}
                    variant="outline"
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:text-white justify-start"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Freight Insights
                  </Button>
                </div>
              </div>

              {/* Recent Quotes */}
              <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">Market Activity</h2>
                  </div>
                </div>

                <div className="space-y-3">
                  {recentQuotes.length > 0 ? recentQuotes.slice(0, 4).map((quote, index) => (
                    <div 
                      key={quote.id || index}
                      className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50"
                    >
                      <div className="p-2 bg-green-500/10 rounded-lg">
                        <TrendingUp className="h-4 w-4 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{quote.origin} → {quote.destination}</p>
                        <p className="text-xs text-slate-500">{quote.cargo_type || 'General Cargo'}</p>
                      </div>
                      {quote.total_price && (
                        <span className="text-sm font-semibold text-green-400">
                          ${quote.total_price?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  )) : (
                    <div className="text-center py-8 bg-gradient-to-br from-orange-900/20 to-amber-900/20 rounded-xl border border-orange-500/20">
                      <div className="p-3 bg-orange-500/10 rounded-full w-fit mx-auto mb-3">
                        <Activity className="h-8 w-8 text-orange-400" />
                      </div>
                      <h3 className="text-white font-medium mb-1">No Recent Activity</h3>
                      <p className="text-slate-400 text-sm max-w-xs mx-auto">
                        Quote activity will appear here as the network grows
                      </p>
                    </div>
                  )}
                </div>

                <Link to="/quotes">
                  <Button variant="ghost" className="w-full mt-4 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10">
                    View All Quotes <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Network Pulse */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-5 w-5 text-cyan-400" />
                  <h3 className="text-base font-semibold text-white">Network Pulse</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Members Online</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-white font-medium">{Math.min(networkStats.active_members || 0, 12)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Active Routes</span>
                    <span className="text-white font-medium">{(networkStats.countries || 1) * 3}+</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Response Rate</span>
                    <span className="text-green-400 font-medium">94%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
